// SCALABILITY RULE — SOURCE DRAWER SYSTEM
// ─────────────────────────────────────────
// When adding a new feature that shows source information:
// 1. Import useSourceDrawerContext from @/contexts/SourceDrawerContext
// 2. Call openSource({ sourceId, title, externalUrl }) — that's it
// 3. Never create a new drawer, modal, or navigation for sources
// 4. Never add window.open for source URLs directly in components
// 5. If the source type needs special display → extend SourceDrawer.tsx
//    by adding a new section, not a new component
//
// This file is the SINGLE source of truth for source display.
// Future source types (podcasts, books, academic papers): add here.

import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SourcePayload {
  sourceId?: string;
  externalUrl?: string;
  title?: string;
  authors?: string;
  citationLocator?: string;
  context?: string;
}

export interface SourceData {
  id: string;
  title: string | null;
  url: string | null;
  authors: string | null;
  source_type: string | null;
  status: string | null;
  decision_reason: string | null;
}

export function useSourceDrawer() {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<SourcePayload | null>(null);
  const [sourceData, setSourceData] = useState<SourceData | null>(null);
  const [loading, setLoading] = useState(false);

  const openSource = useCallback(async (p: SourcePayload) => {
    setPayload(p);
    setOpen(true);
    setSourceData(null);

    if (p.sourceId) {
      setLoading(true);
      const { data } = await supabase
        .from("sources")
        .select("id, title, url, authors, source_type, status, decision_reason")
        .eq("id", p.sourceId)
        .maybeSingle();
      setSourceData(data as SourceData | null);
      setLoading(false);
    }
  }, []);

  const closeSource = useCallback(() => {
    setOpen(false);
    setPayload(null);
    setSourceData(null);
  }, []);

  return { open, payload, sourceData, loading, openSource, closeSource };
}
